import { Formik } from 'formik';
import * as R from 'ramda';
import React, { useMemo, useState } from 'react';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import * as yup from 'yup';

import Page from 'App/components/Page/Page';
import { Button, Col, Row } from 'react-bootstrap';
import PatientHeader from 'App/components/PatientHeader';
import { referralDetailRoute } from 'App/common/routeLookup';
import useFetchPatient from 'App/hooks/useFetchPatient';
import { PERMISSION_TYPES } from 'App/utils/isPermitted/isPermitted';
import useFetchActivityDetails from 'App/hooks/useFetchActivityDetails';
import FormButtonGroup from 'App/components/styles/FormButtonGroup';
import DeleteModal from 'App/components/DeleteModal';
import FAInformationForm from './components/FAInformationForm';
import { convertFAtoSave, convertFAtoView, getFaParams, mapFinancialProgram } from '../referral.utils';
import useUpdateReferralActivityMutation from 'App/hooks/useUpdateReferralActivityMutation';
import toast from 'Shared/toast';
import useDeleteReferralActivity from 'App/hooks/useDeleteReferralActivity';
import BackToLinks from 'App/features/ReferralManager/Referral/BackToLinks';
import SubmitButton from 'Shared/form/SubmitButton';
import useCreateReferralActivityNoteMutation from 'App/hooks/useCreateReferralActivityNoteMutation';
import { completedActivityStatusList, disableActivityStatusList, STATUS_TYPE } from './constants';
import useFetchFAAllPrograms from 'App/hooks/useFetchFAAllPrograms';
import EhrEncounterNoteModal from 'App/components/EHR/EhrEncounterNoteModal';
import useFetchEhrBranchSettings from 'App/hooks/useFetchEhrBranchSettings';
import usePatientEhr from 'App/components/PatientProfile/hooks/usePatientEhr';
import FinancialAssistanceEncounterReviewContent from 'App/features/ReferralManager/Referral/FinancialAssistance/components/EncounterModal/FinancialAssistanceModalReview';
import ReferralActivityHistory from 'App/components/ReferralActivityHistory';
import WarningModal from './components/WarningModal';
import { useUserContext } from 'App/contexts/UserContext';
import EhrProviderNotFoundSection from 'App/components/Assessments/ehr/EhrProviderNotFound';

const propTypes = {};

const defaultProps = {};

const validationSchema = yup.object().shape({
    details: yup.object().shape({
        fundingSourceOther: yup.string().when('fundingSource', {
            is: (option) => option?.id === 'Other',
            then: yup.string().required('Funding Other Source is a required field').nullable(),
            otherwise: yup.string().nullable(),
        }),
        amountFunded: yup
            .number()
            .transform((value, originalValue) => (originalValue === '' ? null : value))
            .nullable()
            .min(0.01, 'Amount Funded must be greater than $0.00')
            .optional(),
    }),
});

function FinancialAssistance() {
    const { patientId, referralId, financialAssistanceId, typeId } = useParams();
    const { isLoading: isLoadingPatient, data: patient } = useFetchPatient(patientId);
    const { ehr, isEhrLoading } = usePatientEhr(patientId);
    const { data: activityDetails, isLoading: isActivityDetailsLoading } = useFetchActivityDetails(
        patientId,
        referralId,
        financialAssistanceId,
        typeId
    );
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const updateReferralActivity = useUpdateReferralActivityMutation();
    const deleteReferralActivity = useDeleteReferralActivity();
    const createReferralActivityNoteMutation = useCreateReferralActivityNoteMutation(financialAssistanceId);
    const { data: branchSettingsData, isLoading: isBranchSettingsLoading } = useFetchEhrBranchSettings();
    const user = useUserContext();
    const userEhrEligible = user?.user_attributes?.ehrProviderId;
    let displayEHRNoteModal = false;
    if (!isBranchSettingsLoading && !isEhrLoading) {
        const { redoxRMDestinationId, redoxRMDestinationName, rmNoteDocumentType } = branchSettingsData;

        displayEHRNoteModal =
            ehr?.patientSynced &&
            redoxRMDestinationId &&
            redoxRMDestinationName &&
            rmNoteDocumentType &&
            userEhrEligible;
    }

    const [ehrModalOpen, setEhrModalOpen] = useState(false);
    const [showWarningModal, setShowWarningModal] = useState(false);
    const params = R.compose(R.pick(['drugs']), getFaParams)(activityDetails);

    const { data: allPrograms, isLoading: isAvailableProgramsLoading } = useFetchFAAllPrograms(
        patientId,
        referralId,
        financialAssistanceId,
        activityDetails?.type,
        activityDetails?.medications,
        params
    );

    const navigate = useNavigate();
    const isLoading = isLoadingPatient || isActivityDetailsLoading || isAvailableProgramsLoading;

    const disableSendToEhrButton = useMemo(() => {
        return R.anyPass([
            R.always(isLoading),
            R.pipe(
                R.propOr([], 'history'),
                R.any((item) => R.includes(item.referralActivityStatus, completedActivityStatusList))
            ),
        ])(activityDetails);
    }, [activityDetails, isLoading]);

    const initialValues = useMemo(() => {
        if (activityDetails && !isActivityDetailsLoading) {
            const FAtoView = convertFAtoView(activityDetails);
            const computedDisableActivity = disableActivityStatusList.includes(FAtoView?.status?.id);
            const computedDisableInputs = FAtoView?.status?.id === STATUS_TYPE.deleted;
            FAtoView.disableActivity = computedDisableActivity;
            FAtoView.disableInputs = computedDisableInputs;
            FAtoView.disableSendToEhrButton = disableSendToEhrButton;

            if (!isAvailableProgramsLoading && allPrograms !== undefined) {
                FAtoView.availablePrograms = R.map(mapFinancialProgram, allPrograms);
            }

            return FAtoView;
        }
        return {};
    }, [activityDetails, allPrograms, isAvailableProgramsLoading, disableSendToEhrButton, isActivityDetailsLoading]);
    const navigateToReferralDetailPage = () => {
        navigate(generatePath(referralDetailRoute, { patientId, referralId }));
    };

    const openEhrEncounterNoteModal = () => {
        setEhrModalOpen(true);
    };

    const deleteActivity = () => {
        deleteReferralActivity
            .mutateAsync({
                patientId,
                referralId,
                activityId: financialAssistanceId,
            })
            .then(() => {
                navigateToReferralDetailPage();
            });
    };
    return (
        <Page
            title={typeId === '5' ? 'Financial Assistance Renewal' : 'Financial Assistance'}
            permissionType={PERMISSION_TYPES.ENABLE_REFERRAL}
            isLoading={isLoading}
        >
            {isLoading ? null : (
                <>
                    <PatientHeader patient={patient} showEhrStatus={false} />
                    <BackToLinks patientId={patientId} referralId={referralId} />
                    {displayEHRNoteModal && (
                        <div>
                            <ReferralActivityHistory activityHistory={activityDetails?.history} />
                        </div>
                    )}

                    <Formik
                        initialValues={initialValues}
                        validationSchema={validationSchema}
                        validateOnChange
                        onSubmit={(values, { setSubmitting }) => {
                            if (values?.details?.amountFunded > 60000.0 && !showWarningModal) {
                                setShowWarningModal(true);
                                setSubmitting(false);
                                return;
                            }
                            setSubmitting(true);
                            const { activityPayload } = convertFAtoSave(values);
                            const promiseArray = [
                                updateReferralActivity.mutateAsync({
                                    patientId,
                                    referralId,
                                    activityId: financialAssistanceId,
                                    data: activityPayload,
                                }),
                            ];
                            promiseArray.push(
                                createReferralActivityNoteMutation.mutateAsync({
                                    note: null,
                                    status: values.status.id,
                                })
                            );

                            return Promise.all(promiseArray).then(() => {
                                toast.success('Activity Information has been updated successfully');
                                if (displayEHRNoteModal) {
                                    openEhrEncounterNoteModal();
                                } else {
                                    navigateToReferralDetailPage();
                                }
                            });
                        }}
                    >
                        {({ isSubmitting, handleSubmit, isValid, values }) => {
                            const isFormReadOnly = values.disableActivity || values.disableInputs || isSubmitting;

                            return (
                                <form onSubmit={handleSubmit} noValidate>
                                    <FAInformationForm isFormReadOnly={isFormReadOnly} />
                                    {!userEhrEligible && !isEhrLoading && ehr?.patientSynced && (
                                        <Row>
                                            <Col md={9}>
                                                {' '}
                                                <EhrProviderNotFoundSection />{' '}
                                            </Col>{' '}
                                        </Row>
                                    )}
                                    <Row>
                                        <Col md={9}>
                                            <FormButtonGroup>
                                                <Button
                                                    variant="danger"
                                                    disabled={isFormReadOnly}
                                                    onClick={() => setShowDeleteModal(true)}
                                                >
                                                    Delete
                                                </Button>
                                                <Button
                                                    variant="outline-secondary"
                                                    onClick={navigateToReferralDetailPage}
                                                >
                                                    Cancel
                                                </Button>
                                                <SubmitButton
                                                    disabled={
                                                        !isValid ||
                                                        isFormReadOnly ||
                                                        (!(
                                                            values?.details?.programId ||
                                                            values?.details?.fundingSource?.id
                                                        ) &&
                                                            [
                                                                STATUS_TYPE.partiallyApproved,
                                                                STATUS_TYPE.approved,
                                                            ].includes(values.status.id))
                                                    }
                                                >
                                                    Save Activity
                                                </SubmitButton>
                                                {displayEHRNoteModal &&
                                                    completedActivityStatusList.includes(initialValues.status?.id) && (
                                                        <Button
                                                            variant="outline-secondary"
                                                            disabled={isSubmitting || values.disableSendToEhrButton}
                                                            onClick={openEhrEncounterNoteModal}
                                                        >
                                                            Send To EHR
                                                        </Button>
                                                    )}
                                            </FormButtonGroup>
                                        </Col>
                                    </Row>
                                    {showWarningModal && (
                                        <WarningModal
                                            onHide={() => {
                                                setShowWarningModal(false);
                                            }}
                                            onSuccess={() => handleSubmit()}
                                            isLoading={isSubmitting}
                                        />
                                    )}
                                    {ehrModalOpen &&
                                        !isActivityDetailsLoading &&
                                        !isBranchSettingsLoading &&
                                        !isEhrLoading &&
                                        values.status.id && (
                                            <EhrEncounterNoteModal
                                                activity={{ ...activityDetails }}
                                                patient={patient}
                                                branchSettings={branchSettingsData}
                                                activityStatus={values.status.id}
                                                onComplete={() => {
                                                    return navigateToReferralDetailPage();
                                                }}
                                            >
                                                <FinancialAssistanceEncounterReviewContent
                                                    patient={patient}
                                                    branchSettings={branchSettingsData}
                                                    medicationList={values.medications}
                                                    financialAssistanceReferralDetails={{
                                                        ...values.details,
                                                        status: values.status.id,
                                                        programName: values.availablePrograms?.find(
                                                            (p) => p.programId === values.details?.programId
                                                        )?.programName,
                                                    }}
                                                />
                                            </EhrEncounterNoteModal>
                                        )}
                                </form>
                            );
                        }}
                    </Formik>

                    {showDeleteModal && (
                        <DeleteModal
                            onHide={() => setShowDeleteModal(false)}
                            show={showDeleteModal}
                            onDelete={deleteActivity}
                            title="Delete Activity"
                            message="Are you sure you want to delete this activity?"
                        />
                    )}
                </>
            )}
        </Page>
    );
}

FinancialAssistance.propTypes = propTypes;
FinancialAssistance.defaultProps = defaultProps;

export default FinancialAssistance;
